// components/AuthPage.tsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
} from 'firebase/auth';
import { auth, googleProvider } from '../firebase/config';
import { usePersistentLogos } from '../hooks/usePersistentLogos';

export type AuthMode = 'existing' | 'new';
type CardShellProps = { children: React.ReactNode };

const CardShell: React.FC<CardShellProps> = ({ children }) => (
  <div className="relative w-full max-w-md">
    <div className="absolute -inset-4 rounded-[28px] bg-gradient-to-br from-brand-orange/10 via-transparent to-transparent" />
    <div className="relative bg-white border border-gray-200 rounded-2xl shadow-[0_10px_30px_rgba(0,0,0,0.08)] overflow-hidden">
      <div className="h-1 bg-brand-orange" />
      <div className="p-6 sm:p-7">{children}</div>
    </div>
  </div>
);

type SegmentedButtonProps = {
  active: boolean;
  label: string;
  onClick: () => void;
  disabled?: boolean;
};

const SegmentedButton: React.FC<SegmentedButtonProps> = ({ active, label, onClick, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={[
      'relative flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold transition',
      'border',
      active
        ? 'border-brand-orange text-brand-grey bg-white shadow-[0_6px_16px_rgba(0,0,0,0.06)]'
        : 'border-gray-200 text-gray-600 bg-gray-50 hover:bg-white',
      disabled ? 'opacity-60 cursor-not-allowed' : '',
    ].join(' ')}
  >
    {active ? <span className="absolute inset-x-2 -bottom-[2px] h-[2px] bg-brand-orange rounded-full" /> : null}
    {label}
  </button>
);

type FieldProps = {
  label: string;
  type: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  autoComplete?: string;
  required?: boolean;
  disabled?: boolean;
  hint?: string | null;
};

const Field: React.FC<FieldProps> = ({
  label,
  type,
  value,
  onChange,
  placeholder,
  autoComplete,
  required,
  disabled,
  hint,
}) => (
  <div className="space-y-1.5">
    <div className="flex items-end justify-between gap-3">
      <div className="text-sm font-semibold text-gray-800">{label}</div>
      {hint ? <div className="text-xs text-gray-500">{hint}</div> : null}
    </div>
    <div className="relative">
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className={[
          'w-full px-4 py-3 rounded-lg border bg-white',
          'text-gray-900 placeholder:text-gray-400',
          'shadow-[0_1px_0_rgba(0,0,0,0.02)]',
          'focus:outline-none focus:ring-2 focus:ring-brand-orange/25 focus:border-brand-orange',
          disabled ? 'opacity-60 cursor-not-allowed' : '',
        ].join(' ')}
        required={required}
        autoComplete={autoComplete}
        disabled={disabled}
      />
    </div>
  </div>
);

type PrimaryButtonProps = {
  children: React.ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit';
  disabled?: boolean;
};

const PrimaryButton: React.FC<PrimaryButtonProps> = ({ children, onClick, type = 'button', disabled }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    className={[
      'w-full px-6 py-3 rounded-lg font-semibold text-white',
      'bg-brand-orange shadow-[0_10px_20px_rgba(0,0,0,0.10)]',
      'hover:brightness-[0.98] active:brightness-[0.96] transition',
      'focus:outline-none focus:ring-2 focus:ring-brand-orange/30',
      disabled ? 'opacity-60 cursor-not-allowed' : '',
    ].join(' ')}
  >
    {children}
  </button>
);

type SecondaryButtonProps = {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
};

const SecondaryButton: React.FC<SecondaryButtonProps> = ({ children, onClick, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={[
      'w-full px-6 py-3 rounded-lg font-semibold',
      'border border-gray-200 bg-white text-gray-800',
      'shadow-[0_6px_16px_rgba(0,0,0,0.05)]',
      'hover:bg-gray-50 transition',
      'focus:outline-none focus:ring-2 focus:ring-brand-orange/20 focus:border-brand-orange',
      disabled ? 'opacity-60 cursor-not-allowed' : '',
    ].join(' ')}
  >
    {children}
  </button>
);

type SubtleButtonProps = {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
};

const SubtleButton: React.FC<SubtleButtonProps> = ({ children, onClick, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={[
      'w-full px-6 py-3 rounded-lg font-semibold',
      'border border-gray-200 bg-gray-50 text-gray-700',
      'hover:bg-white transition',
      'focus:outline-none focus:ring-2 focus:ring-brand-orange/20 focus:border-brand-orange',
      disabled ? 'opacity-60 cursor-not-allowed' : '',
    ].join(' ')}
  >
    {children}
  </button>
);

type AlertProps = { kind: 'error' | 'info'; children: React.ReactNode };

const Alert: React.FC<AlertProps> = ({ kind, children }) => {
  const base = 'mb-4 p-3.5 rounded-lg text-sm border shadow-[0_6px_18px_rgba(0,0,0,0.06)]';
  const cls =
    kind === 'error'
      ? 'bg-red-50/90 text-red-800 border-red-200'
      : 'bg-emerald-50/90 text-emerald-800 border-emerald-200';
  return <div className={`${base} ${cls}`}>{children}</div>;
};

type AuthCardHeaderProps = { title: string; subtitle: string };

const AuthCardHeader: React.FC<AuthCardHeaderProps> = ({ title, subtitle }) => (
  <div className="mb-6">
    <div className="flex items-start justify-between gap-3">
      <h1 className="text-2xl font-bold text-brand-grey leading-tight">{title}</h1>
      <div className="shrink-0">
        <div className="h-10 w-10 rounded-xl border border-gray-200 bg-white shadow-[0_8px_18px_rgba(0,0,0,0.06)] flex items-center justify-center">
          <div className="h-4 w-4 rounded-sm bg-brand-orange" />
        </div>
      </div>
    </div>
    <p className="text-sm text-gray-600 mt-2">{subtitle}</p>
  </div>
);
export const AuthPage: React.FC<{
  mode: AuthMode;
  onModeChange: (m: AuthMode) => void;
}> = ({ mode, onModeChange }) => {
  const params = new URLSearchParams(window.location.search);
  const inviteEmailParam = params.get('inviteEmail');
  const inviteTenantIdParam = params.get('tenantId');

  const { brandLogo } = usePersistentLogos();

  // TEMP: render count for diagnosing typing lag
  const renderCountRef = React.useRef(0);
  renderCountRef.current += 1;
  // eslint-disable-next-line no-console
  console.log('[AuthPage] render', renderCountRef.current);

  // TEMP: mount/unmount logger to detect focus-loss due to remounts
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('[AuthPage] MOUNT');
    return () => {
      // eslint-disable-next-line no-console
      console.log('[AuthPage] UNMOUNT');
    };
  }, []);

  const [email, setEmail] = useState((inviteEmailParam || '').trim());
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const emailLower = useMemo(() => String(email || '').trim().toLowerCase(), [email]);

  const clearMessages = () => {
    setError(null);
    setInfo(null);
  };

  const requireEmail = (): string | null => {
    const e = emailLower;
    if (!e) return null;
    return e;
  };

  const handleGoogle = async () => {
    clearMessages();
    setLoading(true);
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (e: any) {
      setError(e?.message || 'Google sign-in failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleExistingSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessages();

    const eLower = requireEmail();
    if (!eLower) return setError('Enter your email.');
    if (!password) return setError('Enter your password.');

    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, eLower, password);
    } catch (err: any) {
      const msg = String(err?.message || '');
      if (msg.includes('auth/invalid-credential') || msg.includes('INVALID_LOGIN_CREDENTIALS')) {
        setError('Wrong email or password.');
      } else {
        setError(err?.message || 'Sign-in failed.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    clearMessages();

    const eLower = requireEmail();
    if (!eLower) return setError('Enter your email first.');

    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, eLower);
      setInfo('Password reset email sent.');
    } catch (err: any) {
      setError(err?.message || 'Reset password failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleNewUserCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessages();

    const eLower = requireEmail();
    if (!eLower) return setError('Enter your invited email.');
    if (!password || password.length < 6) return setError('Password must be at least 6 characters.');
    if (password !== confirmPassword) return setError('Passwords do not match.');

    setLoading(true);
    try {
      await createUserWithEmailAndPassword(auth, eLower, password);
      setInfo('Account created. Completing setup...');
    } catch (err: any) {
      const code = String(err?.code || '');
      const msg = String(err?.message || '');

      if (code === 'auth/email-already-in-use' || msg.includes('EMAIL_EXISTS')) {
        onModeChange('existing');
        setError('Account already exists. Use Existing User to sign in.');
        return;
      }

      setError(err?.message || 'Create account failed.');
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6">
      <div className="w-full max-w-5xl grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
        <div className="hidden lg:flex relative overflow-hidden rounded-2xl border border-gray-200 bg-gray-50">
          

          <div className="relative p-10 flex flex-col justify-between w-full">
            <div>
              <div className="flex items-center gap-3">
                {brandLogo ? (
                  <img src={brandLogo} alt="Brand logo" className="h-12 w-auto object-contain" />
                ) : (
                  <div className="h-12 w-12 rounded-2xl border border-gray-200 bg-white shadow-[0_8px_18px_rgba(0,0,0,0.06)] flex items-center justify-center">
                    <div className="h-5 w-5 rounded-sm bg-brand-orange" />
                  </div>
                )}

                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gray-200 bg-white shadow-[0_8px_18px_rgba(0,0,0,0.05)]">
                  <span className="h-2 w-2 rounded-full bg-brand-orange" />
                  <span className="text-xs font-semibold text-gray-700">One Size Fits None for Teachers</span>
                </div>
              </div>

              <div className="mt-8">
                <div className="text-4xl font-extrabold text-brand-grey leading-tight">
                  Invitation only access
                </div>

                <div className="mt-5 text-lg text-gray-700 leading-relaxed max-w-md">
                  Entry is by invitation only. You may not be able to log in unless your email has been invited.
                </div>

                <div className="mt-6 text-lg text-gray-700 leading-relaxed max-w-md">
                  To know more or request a demo, contact
                  <a
                    className="ml-2 font-bold text-brand-grey underline decoration-brand-orange/70 underline-offset-4 hover:decoration-brand-orange"
                    href="https://sidbahri.com/contact.html"
                    target="_blank"
                    rel="noreferrer"
                  >
                    sidbahri.com/contact.html
                  </a>
                  .
                </div>
              </div>
            </div>

            <div className="text-xs text-gray-500">
              {inviteTenantIdParam ? (
                <span>
                  Tenant: <span className="font-semibold text-gray-700">{inviteTenantIdParam}</span>
                </span>
              ) : (
                <span>&nbsp;</span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center">
          <CardShell>
            <div className="flex items-center justify-between gap-2 mb-6">
              <div className="flex w-full gap-2 p-1 rounded-xl bg-gray-50 border border-gray-200">
                <SegmentedButton
                  active={mode === 'existing'}
                  label="Existing User"
                  onClick={() => {
                    onModeChange('existing');
                    clearMessages();
                  }}
                  disabled={loading}
                />
                <SegmentedButton
                  active={mode === 'new'}
                  label="New User"
                  onClick={() => {
                    onModeChange('new');
                    clearMessages();
                  }}
                  disabled={loading}
                />
              </div>
            </div>

            {mode === 'existing' ? (
              <>
<AuthCardHeader title="Sign in" subtitle="Use your email and password, or continue with Google." />
                {error ? <Alert kind="error">{error}</Alert> : null}
                {info ? <Alert kind="info">{info}</Alert> : null}

                <form onSubmit={handleExistingSignIn} className="space-y-4">
                  <Field
                    label="Email"
                    type="email"
                    value={email}
                    onChange={setEmail}
                    placeholder="you@school.com"
                    required
                    autoComplete="email"
                    disabled={loading}
                  />

                  <Field
                    label="Password"
                    type="password"
                    value={password}
                    onChange={setPassword}
                    placeholder="Your password"
                    required
                    autoComplete="current-password"
                    disabled={loading}
                  />

                  <PrimaryButton type="submit" disabled={loading}>
                    {loading ? 'Please wait...' : 'Sign in'}
                  </PrimaryButton>
                </form>

                <div className="mt-4 space-y-3">
                  <SecondaryButton onClick={handleGoogle} disabled={loading}>
                    Continue with Google
                  </SecondaryButton>

                  <SubtleButton onClick={handleResetPassword} disabled={loading}>
                    Reset password
                  </SubtleButton>
                </div>
              </>
            ) : (
              <>
<AuthCardHeader title="Create account" subtitle="Invite required. Use the same email that was invited." />
                {error ? <Alert kind="error">{error}</Alert> : null}
                {info ? <Alert kind="info">{info}</Alert> : null}

                <form onSubmit={handleNewUserCreateAccount} className="space-y-4">
                  <Field
                    label="Email"
                    type="email"
                    value={email}
                    onChange={setEmail}
                    placeholder="you@school.com"
                    required
                    autoComplete="email"
                    disabled={loading}
                    hint={inviteEmailParam ? 'Prefilled from invite link' : null}
                  />

                  <Field
                    label="Create password"
                    type="password"
                    value={password}
                    onChange={setPassword}
                    placeholder="Create a password"
                    required
                    autoComplete="new-password"
                    disabled={loading}
                    hint="6+ characters"
                  />

                  <Field
                    label="Confirm password"
                    type="password"
                    value={confirmPassword}
                    onChange={setConfirmPassword}
                    placeholder="Confirm password"
                    required
                    autoComplete="new-password"
                    disabled={loading}
                  />

                  <PrimaryButton type="submit" disabled={loading}>
                    {loading ? 'Please wait...' : 'Create account'}
                  </PrimaryButton>
                </form>

                <div className="mt-4 space-y-3">
                  <SecondaryButton onClick={handleGoogle} disabled={loading}>
                    Continue with Google
                  </SecondaryButton>
                </div>

                {inviteTenantIdParam ? (
                  <div className="mt-5 text-xs text-gray-500">
                    Tenant: <span className="font-semibold text-gray-700">{inviteTenantIdParam}</span>
                  </div>
                ) : null}
              </>
            )}
          </CardShell>
        </div>
      </div>
    </div>
  );
};